<script setup lang="ts">
import { ref, watch } from "vue";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-vue-next";
import route from "ziggy-js";

interface Props {
    eventTypeId?: number;
    groupId?: number;
}

const props = defineProps<Props>();

const loading = ref(false);
const error = ref(false);
const qrCodeData = ref<string | null>(null);

watch(
    () => [props.eventTypeId, props.groupId],
    () => {
        if (!qrCodeData.value) {
            fetchQrCode();
        }
    },
    { immediate: true },
);

async function fetchQrCode() {
    loading.value = true;
    error.value = false;

    try {
        const url = props.groupId
            ? route("app.event-type-groups.qr-code.show", {
                  eventTypeGroup: props.groupId,
              })
            : route("app.event-types.qr-code.show", {
                  eventType: props.eventTypeId,
              });

        const response = await fetch(url);

        if (!response.ok) {
            throw new Error("Failed to fetch QR code");
        }

        const data = await response.json();
        qrCodeData.value = data.qrCode;
    } catch (e) {
        error.value = true;
        console.error("Error fetching QR code:", e);
    } finally {
        loading.value = false;
    }
}

function downloadQrCode() {
    if (!qrCodeData.value) return;

    const identifier = props.groupId
        ? `group-${props.groupId}`
        : `event-type-${props.eventTypeId}`;

    const link = document.createElement("a");
    link.href = qrCodeData.value;
    link.download = `qr-code-${identifier}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}
</script>

<template>
    <div class="gap-4 flex flex-col items-center">
        <p class="text-sm text-iron-600 w-full">
            {{ $t("event_types.share_hub.qr_code.description") }}
        </p>

        <div v-if="loading" class="py-12 flex items-center justify-center">
            <div
                class="h-8 w-8 animate-spin border-z-primary-200 border-t-z-primary-600 rounded-full border-4"
            ></div>
        </div>

        <div v-else-if="error" class="text-red-600 text-center">
            {{ $t("event_types.qr_code.error") }}
        </div>

        <template v-else-if="qrCodeData">
            <div class="rounded-lg bg-white p-4 border-iron-200 border">
                <img :src="qrCodeData" alt="QR Code" class="h-48 w-48" />
            </div>

            <Button @click="downloadQrCode" variant="outline" class="gap-2">
                <Download :size="16" />
                {{ $t("event_types.qr_code.download") }}
            </Button>
        </template>
    </div>
</template>
